import { CheckCircle, RadioButtonUnchecked } from "@mui/icons-material";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import {
  PASSWORD_RULES,
  PasswordStrength,
  countSatisfiedRules,
  getPasswordStrength,
} from "../../utils/password-validation";
import { ALIAS, LOGIN_TOKENS, PRIMITIVE } from "../../tokens/design-tokens";

type Props = {
  /** The current value of the New Password field. */
  password: string;
};

const STRENGTH_COLOR: Record<PasswordStrength, string> = {
  Weak: ALIAS.semantics.error.default,
  Medium: ALIAS.semantics.warning.default,
  Strong: ALIAS.semantics.success.default,
};

/**
 * Real-time password policy feedback (ECRH-5171). Renders below the New
 * Password field: a strength meter plus a checklist that marks each rule as it
 * is satisfied while the user types.
 */
const PasswordRequirements = ({ password }: Props) => {
  const strength = getPasswordStrength(password);
  const satisfied = countSatisfiedRules(password);
  const progress = (satisfied / PASSWORD_RULES.length) * 100;

  return (
    <Box sx={{ width: "100%" }}>
      {strength && (
        <Box sx={{ mb: "12px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: "4px",
            }}
          >
            <Typography
              sx={{
                color: LOGIN_TOKENS.color.rememberMeText,
                fontSize: LOGIN_TOKENS.fontSize.body,
                fontFamily: LOGIN_TOKENS.fontFamily,
              }}
            >
              Password strength
            </Typography>
            <Typography
              sx={{
                color: STRENGTH_COLOR[strength],
                fontSize: LOGIN_TOKENS.fontSize.body,
                fontFamily: LOGIN_TOKENS.fontFamily,
                fontWeight: LOGIN_TOKENS.fontWeight.medium,
              }}
            >
              {strength}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: "6px",
              borderRadius: "4px",
              backgroundColor: PRIMITIVE.color.neutrals[900],
              "& .MuiLinearProgress-bar": {
                backgroundColor: STRENGTH_COLOR[strength],
                borderRadius: "4px",
              },
            }}
          />
        </Box>
      )}

      <Stack spacing="6px">
        {PASSWORD_RULES.map((rule) => {
          const met = rule.test(password);
          return (
            <Box
              key={rule.key}
              sx={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              {met ? (
                <CheckCircle
                  sx={{
                    fontSize: LOGIN_TOKENS.fontSize.iconSm,
                    color: ALIAS.semantics.success.default,
                  }}
                />
              ) : (
                <RadioButtonUnchecked
                  sx={{
                    fontSize: LOGIN_TOKENS.fontSize.iconSm,
                    color: LOGIN_TOKENS.color.checkboxUnchecked,
                  }}
                />
              )}
              <Typography
                sx={{
                  color: met
                    ? ALIAS.semantics.success.default
                    : LOGIN_TOKENS.color.subheadingText,
                  fontSize: LOGIN_TOKENS.fontSize.body,
                  fontFamily: LOGIN_TOKENS.fontFamily,
                  lineHeight: 1.4,
                }}
              >
                {rule.label}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default PasswordRequirements;
